﻿import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CONFIG } from '../../core/config';
import { catchError, finalize } from 'rxjs/operators';
import { ExceptionService } from '../../core/services/exception.service';
import { SpinnerService } from '../../core/spinner/spinner.service';

import { PagedResultModel } from '../../core/models/paged-result.model';
import { OrganizationalUnitQuestionsQuery } from '../../core/models/organizational-unit-questions/organizational-unit-question-query';
import { OrganizationalUnitQuery } from '../../core/models/administration/global-codes/organizational-unit-query';
import { OrganizationalUnitQuestionsBasicData } from '../../core/models/organizational-unit-questions/organizational-unit-question-basic-data';
import { QuestionOrganizationUnit } from '../../core/models/organizational-unit-questions/questions/question-organization-unit';
import { OrganizationalUnitDetails } from '../../core/models/council-members/my-questions/organizational-unit-details';
import { MyQuestion } from '../../core/models/council-members/my-questions/my-question';
import { SessionModel } from '../../core/models/sessions/session-for-dropdown';
import { DropdownItem } from '../../core/models/dropdown-item';

@Injectable({
    providedIn: 'root'
})
export class OrganizationalUnitQuestionsService {

    constructor(private _http: HttpClient,
        private _exceptionService: ExceptionService,
        private _spinnerService: SpinnerService) { }

    getAllOrganizationUnits(orgUnitQuery: OrganizationalUnitQuestionsQuery): Observable<PagedResultModel> {

        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();
        return this._http.post<PagedResultModel>(CONFIG.baseUrls.organizationalUnitQuestions.search, orgUnitQuery, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getOrganizationalUnitBasicDataByID(id: number, langID: number): Observable<OrganizationalUnitQuestionsBasicData> {
        this._spinnerService.show();
        return this._http.get<OrganizationalUnitQuestionsBasicData>(CONFIG.baseUrls.organizationalUnitQuestions.getBasicDataByID + "/"+ id + "/" + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getQuestionsByOrganizationalUnitID(query: OrganizationalUnitQuestionsQuery): Observable<PagedResultModel> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();

        return this._http.post<PagedResultModel>(CONFIG.baseUrls.organizationalUnitQuestions.getQuestionsByOrganizationalUnitID, query, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    saveQuestion(data: QuestionOrganizationUnit): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.organizationalUnitQuestions.saveQuestion, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getOrganizationalUnitByUserID(langID: number): Observable<OrganizationalUnitDetails> {
        this._spinnerService.show();
        return this._http.get<OrganizationalUnitDetails>(CONFIG.baseUrls.organizationalUnitQuestions.getOrganizationalUnitByUserID + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getMyQuestionByID(id: number, langID: number): Observable<MyQuestion> {
        this._spinnerService.show();
        return this._http.get<MyQuestion>(CONFIG.baseUrls.organizationalUnitQuestions.getMyQuestionByID + id + "/" + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    saveMyAnswer(data: MyQuestion): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.organizationalUnitQuestions.saveMyAnswer, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getAllOrganizationalUnitsForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllOrganizationalUnitsForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    GetNumberOfOrganizationalUnitQuestionsAndAnswers(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.organizationalUnitQuestions.GetNumberOfOrganizationalUnitQuestionsAndAnswers, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    } 
    getOrganizationalUnitListByAnswerTime(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.organizationalUnitQuestions.getOrganizationalUnitListByAnswerTime, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    } 
    getNumberOfAnsweredAndNotAnsweredQuestions(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.organizationalUnitQuestions.getNumberOfAnsweredAndNotAnsweredQuestions, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getNumberOfQuestionsByGender(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.organizationalUnitQuestions.getNumberOfQuestionsByGender, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getNumberOfQuestionsBySensitiveCategory(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.organizationalUnitQuestions.getNumberOfQuestionsBySensitiveCategory, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    } 
    getOrganizationalUnitListAverageAnswerTime(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.organizationalUnitQuestions.getOrganizationalUnitListAverageAnswerTime, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getNumberOfQuestionsByYearGroup(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.organizationalUnitQuestions.getNumberOfQuestionsByYearGroup, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
}